/**
 * useOnboarding — orchestrator for the D-rebuild discovery flow (Stream C).
 *
 * State machine:
 *
 *   dormant ─Begin─► igniting ─1.2s─► live ─all-succeeded─► completing ─1.7s─► welcomed
 *                                       │
 *                                       └─ Esc ──────────────────────────────► welcomed
 *
 * The hook owns timing only. Server state — the real `/user-graph` payload
 * and the live-progress stream from `/user-graph/discovery-status` — belongs
 * to TanStack Query (Stream D). Until Stream D's MSW handlers ship, the
 * sequencer here drives connector states with a deterministic setTimeout
 * cadence so the visual contract holds in dev and tests.
 *
 * Reduced-motion shortcut: tests can advance fake timers; the cadence still
 * resolves quickly. We do not auto-collapse the discovery scene with
 * animations during tests beyond what the timers express.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ME_QUERY_KEY } from '@/contexts/AuthContext';
import type { Me } from '@/api/generated';
import {
  CONNECTOR_IDS,
  type ConnectorRow,
  ONBOARDING_STAGES,
  type OnboardingStage,
  useOnboardingStore,
} from '@/stores/onboarding.store';

/** Stagger between successive connectors entering `running`. */
const RUN_STAGGER_MS = 280;
/** Time each connector spends in `running` before flipping to `succeeded`. */
const RUN_DURATION_MS = 720;
/** Wait between dormant → igniting → live. */
export const IGNITION_MS = 1_200;
/** Visual collapse window (completing → welcomed). */
export const COMPLETION_MS = 1_700;

interface UseOnboardingReturn {
  readonly me: Me | undefined;
  readonly stage: OnboardingStage;
  readonly connectors: ReadonlyArray<ConnectorRow>;
  readonly userName: string;

  /** dormant → igniting → live, kicks the sequencer. */
  readonly beginDiscovery: () => void;
  /** Force-skip directly to `welcomed`. Used by Esc + tests. */
  readonly skipToWelcomed: () => void;
  /** Cancel any pending sequencer timers without changing the stage. */
  readonly cancelSequencer: () => void;
}

export function useOnboarding(): UseOnboardingReturn {
  const queryClient = useQueryClient();
  const me = queryClient.getQueryData<Me>(ME_QUERY_KEY);

  const stage = useOnboardingStore((s) => s.stage);
  const setStage = useOnboardingStore((s) => s.setStage);
  const connectorsMap = useOnboardingStore((s) => s.connectors);
  const setConnector = useOnboardingStore((s) => s.setConnector);

  // Sequencer timers. Map keyed by purpose so cancel + restart is idempotent.
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const cancelSequencer = useCallback(() => {
    for (const t of timersRef.current.values()) clearTimeout(t);
    timersRef.current.clear();
  }, []);

  useEffect(() => () => cancelSequencer(), [cancelSequencer]);

  /**
   * Run the connector cascade. Each connector gets:
   *   - flipped to `running` at `i * RUN_STAGGER_MS`
   *   - flipped to `succeeded` `RUN_DURATION_MS` later
   *
   * After the last `succeeded` writes, we set stage = completing and queue
   * one more timer for the welcomed transition.
   */
  const startSequencer = useCallback(() => {
    cancelSequencer();
    for (let i = 0; i < CONNECTOR_IDS.length; i += 1) {
      const id = CONNECTOR_IDS[i];
      if (id === undefined) continue;
      const startAt = i * RUN_STAGGER_MS;
      const endAt = startAt + RUN_DURATION_MS;

      const tStart = setTimeout(() => {
        setConnector(id, { state: 'running', startedAt: Date.now() });
      }, startAt);
      timersRef.current.set(`${id}:running`, tStart);

      const isLast = i === CONNECTOR_IDS.length - 1;
      const tDone = setTimeout(() => {
        setConnector(id, { state: 'succeeded' });
        if (isLast) {
          setStage('completing');
          const tWelcome = setTimeout(() => {
            setStage('welcomed');
          }, COMPLETION_MS);
          timersRef.current.set('welcome', tWelcome);
        }
      }, endAt);
      timersRef.current.set(`${id}:done`, tDone);
    }
  }, [cancelSequencer, setConnector, setStage]);

  const beginDiscovery = useCallback(() => {
    if (stage !== 'dormant') return;
    setStage('igniting');
    const t = setTimeout(() => {
      setStage('live');
      startSequencer();
    }, IGNITION_MS);
    timersRef.current.set('ignition', t);
  }, [setStage, stage, startSequencer]);

  const skipToWelcomed = useCallback(() => {
    cancelSequencer();
    // Mark every still-pending or running connector as `succeeded` so the
    // welcomed state's narrative ("we found everything") still reads.
    for (const id of CONNECTOR_IDS) {
      const row = useOnboardingStore.getState().connectors[id];
      if (row && row.state !== 'succeeded' && row.state !== 'failed') {
        setConnector(id, { state: 'succeeded' });
      }
    }
    setStage('welcomed');
  }, [cancelSequencer, setConnector, setStage]);

  const connectors = CONNECTOR_IDS.map(
    (id) =>
      connectorsMap[id] ?? {
        id,
        label: id,
        state: 'pending' as const,
      },
  );

  // Greeting falls back gracefully when the user query hasn't hydrated.
  const firstName = (me?.name ?? '').split(/\s+/)[0] ?? '';
  const userName = firstName.length > 0 ? firstName : 'there';

  return {
    me,
    stage,
    connectors,
    userName,
    beginDiscovery,
    skipToWelcomed,
    cancelSequencer,
  };
}

export { ONBOARDING_STAGES };
